import React, { useState, useEffect } from 'react';
//import HeaderColumns, { renderField, getNestedPropertyValue } from '../headercolumns.js';
import { FormTypes } from "../../models";
import { ReportType, ReportDataSource, ReportFormat, ViewType, ReportColumnSourceType, LambdaURLs } from '../../Enums.js';
import { saveAs } from 'file-saver';
import { getUrl } from "aws-amplify/storage";
import { handleError } from "../../helpers/LogHelpers.js";
import { Document, Packer, Paragraph, TextRun, ImageRun } from 'docx';
import * as XLSX from 'xlsx';  // Import XLSX for Excel file generation
import Handlebars from 'handlebars';
import jsPDF from 'jspdf';
import Countries from '../countries.js';  // Adjust the path based on your directory structure
import html2pdf from 'html2pdf.js';
import JSZip from 'jszip';
import './DownloadOptions.css';  // Assuming we put the CSS in this file
import { Form } from 'react-router-dom';

const DownloadOptions = ({
  viewfieldsInstance,
  fields,
  rows,
  allSubmissions,
  submissionToken,
  reviewingTokens,
  availableReports,
  projectCode,
  viewLabel,
  formType,
  viewType,
  //selectedView,
  appUser,
  isDownloadOptionsOpen  // Received from parent to control expansion
}) => {
  const [expanded, setExpanded] = useState(false);  // Local state to track visibility of report list
  

  Handlebars.registerHelper('plusOne', function(value) {
    return parseInt(value) + 1;
  });

  // Register a helper to extract countries from their 2 digit code
  Handlebars.registerHelper('countryNameFromCode', function (code) {
    const countryName = Object.keys(Countries).find(key => Countries[key] === code);
    return countryName || 'Unknown Country';  // Return the country name or 'Unknown Country' if the code is not found
  });

  // Register a helper to format date strings
  Handlebars.registerHelper('formatDate', function(dateString) {
    if (!dateString) return 'N/A'; // Handle empty cases

    const options = { day: 'numeric', month: 'long', year: 'numeric' }; // Full month name
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', options); // 'en-GB' for British English formatting
  });

  // Register the helper to get the signed URL for a specific file
  Handlebars.registerHelper('getFileUrl', function(dataSource, dataKey) {
    
    // Check if dataSource is valid
    if (!Array.isArray(dataSource)) {
        console.warn(`Invalid dataSource for dataKey: ${dataKey}. Expected an array.`);
        return new Handlebars.SafeString(`<span>No files available</span>`);
    }

    // Find the file in the dataSource array that matches the dataKey
    const myFile = dataSource.find(file => file.dataKey === dataKey);
    
    if (myFile) {
        // Return a placeholder link
        return new Handlebars.SafeString(`<span class="file-link" file-key="${myFile.fileKey}" data-key="${myFile.dataKey}">${myFile.name || 'Download File'}</span>`);
    }

    return new Handlebars.SafeString(`<span>No file found for ${dataKey}</span>`);
  });

  // Effect to handle expansion state changes
  useEffect(() => {
    // Immediately apply or remove the expanded class based on the prop
    setExpanded(isDownloadOptionsOpen);
  }, [isDownloadOptionsOpen]);



  const downloadReport = async (report) => {
    try {
      let fieldDefinitions = [];
      let processedData = [];
      let fileName = "";

      switch (report.type) {
        case ReportType.ALL_VIEW: {
          fieldDefinitions = fields;
          processedData = await processFieldsForDownload(true, fieldDefinitions, report);
          break;
        }

        case ReportType.VISIBLE_VIEW: {
          fieldDefinitions = (fields ?? []).filter((f) => f.visible === true);
          processedData = await processFieldsForDownload(false, fieldDefinitions, report);
          break;
        }

        case ReportType.BESPOKE_COLUMNS: {
          fieldDefinitions = report.fieldDefinitions;
          if (report.dataSource === "Submissions&Reviews") {
            processedData = await processSubmissionsAndReviewsForDownload(fieldDefinitions, report.maxReviews);
          } else {
            processedData = await processFieldsForDownload(true, fieldDefinitions, report);
          }
          break;
        }

        case ReportType.SINGLE_TEMPLATE: {
          // Process precisely one submission with a raw template string.
          // Caller supplies `report.template` and `report.fileName`.
          const singleTemplate = report.template;
          const one = rows && rows.length ? rows[0] : null;
          if (!one) throw new Error("SINGLE_TEMPLATE: no row available to render.");
          await processSubmissionData(singleTemplate, one, report.fileName || "submission");
          // SINGLE_TEMPLATE renders/saves internally; nothing to download here.
          return;
        }

        case ReportType.TEMPLATE: {
          const rawData = await processReportData(report, false);
          switch (report.format) {
            case ReportFormat.PDF:
              processedData = generatePdfBook(rawData);
              break;
            case ReportFormat.DOCX:
              processedData = await generateDocxBook(rawData);
              break;
            case ReportFormat.HTML:
              processedData = generateHTMLDoc(rawData);
              break;
            default:
              throw new Error(`Unsupported format: ${report.format}`);
          }
          break;
        }

        case ReportType.FILTER: {
          fieldDefinitions = fields.filter(f => f.fieldType === report.fieldFilter);
          const filterFlag = Array.isArray(report.filter);
          processedData = filterFlag ? processFilter(rows, report.filter) : [...rows];
          break;
        }

        case ReportType.BESPOKE: {
          processedData = await processBespokeReport(report);
          break;
        }

        default:
          throw new Error(`Report type not defined: ${report.type}`);
      }

      // Dispatch by format
      switch (report.format) {
        case ReportFormat.EXCEL: {
          const label = report.fileName ? report.fileName : viewLabel;
          await downloadExcel(processedData, label);
          break;
        }
        case ReportFormat.CSV:
          downloadCSV(processedData);
          break;
        case ReportFormat.PDF: {
          const out = report.fileName ? `${report.fileName}.pdf` : "report.pdf";
          downloadPDF(processedData, out);
          break;
        }
        case ReportFormat.DOC:
          downloadDOC(processedData);
          break;
        case ReportFormat.DOCX: {
          const out = report.fileName ? `${report.fileName}.docx` : "report.docx";
          downloadDOCX2(processedData, out);
          break;
        }
        case ReportFormat.HTML:
          downloadHTML(processedData);
          break;
        case ReportFormat.ZIP:
          await downloadZIP(fieldDefinitions, processedData);
          break;
        default:
          throw new Error(`Unsupported format: ${report.format}`);
      }
    } catch (error) {
      console.error("Error processing report:", error);
    }
  };


/**
 * Split global allSubmissions into four sets based on hidden flag and formToken.
 *
 * @param {Object} [options={}]
 * @param {boolean} [options.includeSubmissions=true]
 * @param {boolean} [options.includeReviews=true]
 * @param {boolean} [options.includeHiddenSubmissions=true]
 * @param {boolean} [options.includeHiddenReviews=true]
 * @returns {Object}
 *   { submissions, reviews, hiddenSubmissions, hiddenReviews }
 */
function splitItems(options = {}) {
  const {
    includeSubmissions = true,
    includeReviews = true,
    includeHiddenSubmissions = true,
    includeHiddenReviews = true,
  } = options;

  // Log total count
  console.log(`Total allSubmissions: ${allSubmissions.length}`);

  // Partition visible vs hidden
  const visibleItems = allSubmissions.filter(item => !item.hidden);
  const hiddenItems  = allSubmissions.filter(item => item.hidden);

  console.log(`Visible items: ${visibleItems.length}`);
  console.log(`Hidden items:  ${hiddenItems.length}`);

  // Further split by token
  const submissionsSubset       = visibleItems.filter(item => item.formToken === submissionToken);
  const reviewsSubset           = visibleItems.filter(item => reviewingTokens.includes(item.formToken));
  const hiddenSubmissionsSubset = hiddenItems.filter(item => item.formToken === submissionToken);
  const hiddenReviewsSubset     = hiddenItems.filter(item => reviewingTokens.includes(item.formToken));

  console.log(`  → submissions (visible):       ${submissionsSubset.length}`);
  console.log(`  → reviews (visible):           ${reviewsSubset.length}`);
  console.log(`  → hiddenSubmissions:           ${hiddenSubmissionsSubset.length}`);
  console.log(`  → hiddenReviews:               ${hiddenReviewsSubset.length}`);

  // Sanity check
  const totalPieces = submissionsSubset.length
                    + reviewsSubset.length
                    + hiddenSubmissionsSubset.length
                    + hiddenReviewsSubset.length;
  console.log(`Sum of subsets: ${totalPieces}` +
              (totalPieces === allSubmissions.length ? ' ✅' : ' ⚠️ mismatch!'));

  return {
    submissions:       includeSubmissions       ? submissionsSubset       : [],
    reviews:           includeReviews           ? reviewsSubset           : [],
    hiddenSubmissions: includeHiddenSubmissions ? hiddenSubmissionsSubset : [],
    hiddenReviews:     includeHiddenReviews     ? hiddenReviewsSubset     : [],
  };
}


/**
 * Process a flat list of submissions & reviews into rows ready for download.
 *
 * @param {Array<Object>} updatedFields
 *   Array of column definitions. Each definition must include:
 *   - source (ReportColumnSourceType.SUBMISSION, .REVIEW, .DERRIVED)
 *   - headerName, index, fieldType (for derived), attributes.contributingFields, sort, etc.
 * @param {number} [maxReviews=3]
 *   Number of review slots to allocate per submission.
 *
 * @returns {Array<Object>}
 *   Array of processed rows, with columns ordered and rows sorted per definitions.
 */
async function processSubmissionsAndReviewsForDownload(
  updatedFields,
  maxReviews = 3
) {
  console.log("Processing submissions and their associated reviews");

  // 1&2) Grab only visible submissions+reviews
  const { submissions, reviews } = splitItems({
    includeHiddenSubmissions: false,
    includeHiddenReviews:     false
  });

  // 3) Prepare containers
  const processedRows = [];
  const dynamicFields = [];

  // 4) Split column definitions by source
  const submissionFields = updatedFields.filter(field => field.source === ReportColumnSourceType.SUBMISSION);
  const derivedFields    = updatedFields.filter(field => field.source === ReportColumnSourceType.DERRIVED);
  const reviewFields     = updatedFields.filter(field => field.source === ReportColumnSourceType.REVIEW);

  // 5) Build rows
  submissions.forEach(submission => {
    const newRow = {};

    // 5a) Submission fields
    submissionFields.forEach(field => {
      newRow[field.headerName] = getValueFromObject(submission, field);
      if (!dynamicFields.some(dynamicField => dynamicField.headerName === field.headerName)) {
        dynamicFields.push({ headerName: field.headerName, index: field.index, reviewIteration: null });
      }
    });

    // 5b) Review fields (up to maxReviews) N.B. ToDo This is not how we get linked reviews
    const associatedReviews = reviews.filter(
      review => (review.submission?.id || review.data?.submission?.id) === submission.id
    );
    for (let i = 0; i < maxReviews; i++) {
      const review = associatedReviews[i] || {};
      reviewFields.forEach(field => {
        const header = `${field.headerName} (Review ${i + 1})`;
        newRow[header] = getValueFromObject(review, field);
        if (!dynamicFields.some(dynamicField => dynamicField.headerName === header)) {
          dynamicFields.push({ headerName: header, index: field.index, reviewIteration: i });
        }
      });
    }

    // 5c) Derived fields
    derivedFields.forEach(field => {
      let value = 0;
      const fields = field.attributes.contributingFields;
      switch (field.fieldType) {
        case "total":
          value = fields.reduce((sum, f) => {
            const v = Number(newRow[f]);
            return sum + (isNaN(v) ? 0 : v);
          }, 0);
          break;
        case "average":
          const nums = fields
            .map(f => Number(newRow[f]))
            .filter(v => !isNaN(v));
          value = nums.length
            ? parseFloat((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2))
            : 0;
          break;
        case "standardDeviation":
          const vals = fields
            .map(f => Number(newRow[f]))
            .filter(v => !isNaN(v));
          if (vals.length > 1) {
            const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
            const variance = vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length;
            value = Math.sqrt(variance);
          }
          break;
      }
      newRow[field.headerName] = value;
      if (!dynamicFields.some(dynamicField => dynamicField.headerName === field.headerName)) {
        dynamicFields.push({ headerName: field.headerName, index: field.index, reviewIteration: null });
      }
    });

    processedRows.push(newRow);
  });

  // 6) Order dynamicColumns
  dynamicFields.sort((a, b) => {
    if (a.reviewIteration !== null && b.reviewIteration !== null) {
      if (a.reviewIteration !== b.reviewIteration) {
        return a.reviewIteration - b.reviewIteration;
      }
      return a.index - b.index;
    }
    if (a.reviewIteration === null && b.reviewIteration !== null) return -1;
    if (a.reviewIteration !== null && b.reviewIteration === null) return 1;
    return a.index - b.index;
  });

  // 7) Project rows onto ordered headers
  const finalProcessedRows = processedRows.map(row => {
    const sortedRow = {};
    dynamicFields.forEach(field => {
      sortedRow[field.headerName] = row[field.headerName];
    });
    return sortedRow;
  });

  // 8) Sort rows themselves
  const sortDefs = updatedFields
    .filter(col => col.sort)
    .sort((a, b) => (a.sort.order || 0) - (b.sort.order || 0));

  finalProcessedRows.sort((a, b) => {
    for (const col of sortDefs) {
      const field = col.headerName;
      if (field === "#") {
        const numA = parseInt(a["#"], 10) || Infinity;
        const numB = parseInt(b["#"], 10) || Infinity;
        if (numA !== numB) return numA - numB;
        return (parseFloat(b["Progress"]) || 0) - (parseFloat(a["Progress"]) || 0);
      }
      const dir = col.sort.direction === "asc" ? 1 : -1;
      if (a[field] < b[field]) return -1 * dir;
      if (a[field] > b[field]) return 1 * dir;
    }
    return 0;
  });

  console.log("Processed and sorted rows for download:", finalProcessedRows);
  return finalProcessedRows;
}


  const processFilter = (rows, filter) => {
    try {
      //console.log("rows", rows);
      //console.log("filter", filter);
  
      // Ensure rows is an array and filter is an array
      if (!Array.isArray(rows) || !Array.isArray(filter)) {
        throw new Error("Invalid arguments provided to processFilter. 'rows' and 'filter' must be arrays.");
      }
  
      // Helper function to get the nested value based on a path
      const getNestedValue = (obj, path) => {
        try {
          return path.split('.').reduce((acc, part) => {
            return acc && acc[part] !== undefined ? acc[part] : null; // Traverse the path
          }, obj);
        } catch (error) {
          console.error("Error getting nested value for path:", path, error);
          return null;
        }
      };
  
      // Filter rows based on the filter criteria
      let filteredRows = [...rows]; // Start with a copy of the rows to avoid mutating the original
  
      filter.forEach((filterCondition, index) => {
        try {
          const { operator, field, value } = filterCondition;
  
          //console.log("operator", operator);
          //console.log("field", field);
          //console.log("value", value);
  
          // Apply the appropriate filtering logic based on the operator
          filteredRows = filteredRows.filter(row => {
            try {
              let fieldValue = getNestedValue(row, field); // Get the value from the nested path
              //console.log("fieldValue", fieldValue);
  
              if (operator === "equals") {
                // Check if the field matches the value exactly
                return fieldValue === value;
              } 
              else if (operator === "contains") {
                // Check if the field contains the value (assuming string comparison)
                return String(fieldValue).includes(value);
              }
              else if (operator === "greaterThan") {
                // Check if the field is greater than the value (numeric comparison)
                return fieldValue > value;
              }
              else if (operator === "lessThan") {
                // Check if the field is less than the value (numeric comparison)
                return fieldValue < value;
              }
              // Add more operators as needed
  
              return true; // Default case, no filtering applied
            } catch (innerError) {
              console.error("Error processing filter condition at index", index, innerError);
              return false;  // In case of error in a specific row, we exclude it from the result
            }
          });
        } catch (error) {
          console.error("Error processing filter condition at index", index, error);
          throw error;  // Re-throw the error to the outer catch block
        }
      });
  
      // Return the filtered rows
      //console.log("filteredRows", filteredRows);
      return filteredRows;
  
    } catch (outerError) {
      console.error("Error in processFilter function:", outerError);
      // Handle or rethrow the error if necessary
      return rows;  // Return the original rows unmodified if there's an error
    }
  };
  
  // Function to process columns and rows for download
  const processFieldsForDownload = async (allRows = true, updatedFields, report) => {
    //console.log("Processing columns and rows for a download format");
    console.log("Rows", rows);

    let includeSubmissions = false;
    let includeReviews = false;
    let includeHiddenSubmissions = false;
    let includeHiddenReviews = false;
    let itemsToProcess = [];

    if (allRows) {
      const dataSource = report.dataSource || formType;
      switch (dataSource) {
        case ReportDataSource.SUBMISSION:
          includeSubmissions = true;
          break;
      
        case ReportDataSource.REVIEW:
          includeReviews = true;
          break;
      
        case ReportDataSource.SUBMISSION_AND_REVIEW:
          includeSubmissions = true;
          includeReviews     = true;
          break;
      
        default:
          // neither
      }

      const { submissions, reviews } = splitItems({
        includeSubmissions,
        includeReviews,
        includeHiddenSubmissions,
        includeHiddenReviews,
      });

      itemsToProcess = [...submissions, ...reviews];
    } else {
      // If not all rows, we assume rows is already filtered
      itemsToProcess = rows;
    }

    const downloadColumns = updatedFields;
    const processedRows = [];

    const filteredRows = await filterAndSortData(itemsToProcess, report);

    // Loop over each row
    filteredRows.forEach((row) => {
      let newRow = {};

      // Check for valid ID
      if (!row.id) {
        throw new Error(`Row with tracking number ${row.meta?.trackingNumber || "unknown"} is missing an ID.`);
      }

      // Process each field
      downloadColumns.forEach((field) => {
        if (field.fieldType === "authors") {
          // Handle authors concatenation
          const authorsArray = getNestedPropertyValue(row, field.dataSource);
          if (Array.isArray(authorsArray)) {
            const concatenatedAuthors = authorsArray.map((author) => {
              const givenName = author.givenName || "";
              const familyName = author.familyName || "";
              const affiliations = author.affiliations || [];
              const affiliationNumbers = affiliations.map((aff) => `<sup>${aff + 1}</sup>`).join(""); // Increment affiliations by 1
              return `${givenName} ${familyName}${affiliationNumbers}`.trim();
            }).join(", ");
            newRow[field.headerName] = concatenatedAuthors;
          } else {
            newRow[field.headerName] = "";
          }
        } else if (field.fieldType === "affiliations") {
          // Handle affiliations concatenation
          const affiliationsArray = getNestedPropertyValue(row, "data.affiliations");
          if (Array.isArray(affiliationsArray)) {
            const concatenatedAffiliations = affiliationsArray.map((affiliation, index) => {
              const institution = affiliation.institution || "";
              const country = affiliation.country || "";
              return `<sup>${index + 1}</sup>${institution} ${country}`.trim();
            }).join(", ");
            newRow[field.headerName] = concatenatedAffiliations;
          } else {
            newRow[field.headerName] = "";
          }
        } else if (field.fieldType === "file") {
          const fileDataKey = field.field;
          const isUploaded = row.files && row.files.some(file => file.dataKey === fileDataKey);
      
          if (isUploaded) {
              const submissionId = row.id;
              const lambdaUrl = `${LambdaURLs.GET_FILE}?submissionId=${submissionId}&fileDataKey=${fileDataKey}`;
      
              // Excel-friendly formula for the hyperlink
              const cellValue = `=HYPERLINK("${lambdaUrl}", "Download File")`;  // Mark as formula
      
              // Apply styling for hyperlinks (blue color and underlined)
              newRow[field.headerName] = {
                  f: cellValue,  // Formula
                  s: {  // Styling
                      font: {
                          color: { rgb: "0000FF" },  // Blue color for the link
                          underline: true,  // Underline the text
                      },
                  },
              };
          } else {
              newRow[field.headerName] = "No";
          }       
        } else if (field.dataSource && field.dataSource.startsWith("meta.owner")) {
          let ownerData = row.meta?.owner;

          if (typeof ownerData === "string") {
            try {
              ownerData = JSON.parse(ownerData);
            } catch (error) {
              console.error("Error parsing owner data:", error);
              ownerData = null;
            }
          }

          if (ownerData && typeof ownerData === 'object') {
            let field = field.field;
            if (field === "first_name") field = "givenName";
            if (field === "last_name") field = "familyName";
            newRow[field.headerName] = ownerData[field] || "";
          } else {
            newRow[field.headerName] = "";
          }
        } else if (
          field.dataSource &&
          (field.dataSource.startsWith("submission.meta.owner") ||
            // TODO(dan): data.submission.meta.owner relies on the review-side
            // data.submission write-back, which has been removed (reviews are now
            // stored as JSON in S3, not embedded in Submissions rows). This will read
            // stale/missing owner data going forward — needs to source owner data from
            // the review's S3 asset (or its linkedData) instead.
            field.dataSource.startsWith("data.submission.meta.owner"))
        ) {
          let secondaryOwnerData = row.submission?.meta?.owner || row.data?.submission?.meta?.owner;

          if (secondaryOwnerData && typeof secondaryOwnerData === 'object') {
            let field = field.field;
            if (field === "app_first_name") field = "givenName";
            if (field === "app_last_name") field = "familyName";
            if (field === "app_email") field = "email";
            newRow[field.headerName] = secondaryOwnerData[field] || "";
          } else {
            newRow[field.headerName] = "";
          }
        } else if (field.fieldType === 'fixed') {
          newRow[field.headerName] = field.value;
        } else if (field.fieldType === 'index') {
          newRow[field.headerName] = field.value;
        } else {
          const value = getNestedPropertyValue(row, field.dataSource);
          newRow[field.headerName] = value !== undefined ? value : "";
        }

        // Apply prefix and suffix if they exist
        if (field.prefix || field.suffix) {
          const currentValue = newRow[field.headerName] || "";
          const prefix = field.prefix || "";
          const suffix = field.suffix || "";
          newRow[field.headerName] = `${prefix}${currentValue}${suffix}`;
        }
      });

        processedRows.push(newRow); // Add the new row
    });

    //console.log("Processed and sorted rows: ", processedRows);
    return processedRows;
  };

  // Generic function to process report data
  const filterAndSortData = async (initialData, report) => {
    try {
      let filteredData = [];
      const filterFlag = report.filter && Array.isArray(report.filter); // Check if any item has the "filter" property
      filteredData = filterFlag ? processFilter(initialData, report.filter) : [...initialData];

      // Check if sort exists, otherwise default to sorting by "id" in ascending order
      const { column = 'meta.trackingNumber', direction = 'asc', number = true } = report.sort || {};

      // Sort submissions based on the report.sort configuration
      const sortedSubmissions = filteredData.sort((a, b) => {
        let valueA = getNestedPropertyValue(a, column);
        let valueB = getNestedPropertyValue(b, column);

        // Handle number sorting
        if (number) {
          valueA = valueA ? parseInt(valueA, 10) : Infinity;
          valueB = valueB ? parseInt(valueB, 10) : Infinity;
        } else {
          // Handle alpha sorting
          valueA = valueA ? String(valueA).toLowerCase() : '';
          valueB = valueB ? String(valueB).toLowerCase() : '';

          if (valueA === '' && valueB !== '') return 1;
          if (valueB === '' && valueA !== '') return -1;
        }

        // Handle sorting based on direction (asc or desc)
        if (number) {
          return direction === "asc" ? valueA - valueB : valueB - valueA;
        } else {
          return direction === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
        }
      });     
      
      return sortedSubmissions;  // Return processed data to be formatted in specific formats

    } catch (error) {
      console.error("Error processing report data:", error);
      return [];
    }
  };
    
  // Helper to get and log abstract code
  const getBlankParagraph = () => {    
    
    return new Paragraph({
      children: [new TextRun({ text: '' })],
      pageBreakBefore: true,  // Add page break if needed
    });
  };

  // Function to fetch image data from URL and return as a buffer
  const fetchImageAsBuffer = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      
      if (response.ok) {
        const blob = await response.blob();  // Use .blob() in the browser
        const arrayBuffer = await blob.arrayBuffer();  // Convert blob to arrayBuffer
        return arrayBuffer;  // Return the arrayBuffer (which represents the image data)
      } else {
        console.error("Error fetching image (response not ok):", response.statusText);
        return null;
      }
    } catch (error) {
      console.error("Error fetching image (error thrown):", error);
      return null;
    }
  };

  // Main function to parse HTML and create DOCX
  const parseHtmlBodyToDocx = async (htmlString) => {
    //console.log("htmlString", htmlString);
    const paragraphs = [];
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    // Extract image URLs from the HTML string
    const imageUrls = [];
    
    // Modified handleChildNodes to process both current node and its childNodes
       // Function to handle child nodes within a block (e.g., within a <p> tag)
       const handleChildNodes = (parentNode) => {
        const textRuns = [];
        parentNode.childNodes.forEach((child) => {
          const textContent = child.textContent || "";
  
          switch (child.tagName) {
            case "SUP":
              // Handle superscript text
              textRuns.push(new TextRun({ text: textContent, superScript: true }));
              break;
            default:
              // Handle normal text
              textRuns.push(new TextRun({ text: textContent }));
          }
        });
        return textRuns;
      };


     // Modified handleChildNodes to process both current node and its childNodes
     const buildImageUrls = (parentNode) => {
      const textRuns = [];

      // Process the current node itself
      if (parentNode.nodeType === 1) {  // Only process element nodes
        const textContent = parentNode.textContent || "";
        
        if (parentNode.tagName === "IMG") {
          // Only process the IMG tag if it has a valid src attribute
          if (parentNode.src && parentNode.src.startsWith("http")) {
            const imageUrl = parentNode.src;
            imageUrls.push(imageUrl);
          }
        }
      }
    };



    // Process all nodes inside tempDiv and extract text and image URLs
    for (const node of tempDiv.childNodes) {
      buildImageUrls(node);
    }

    
    // Fetch all images in parallel
    const imageBuffers = await Promise.all(
      imageUrls.map((url) => fetchImageAsBuffer(url))
    );

    let imageIndex = 0;
    const maxWidth = 500;  // You can change this value to fit your requirements

    // Now that we have all images, create paragraphs for each image
    for (const node of tempDiv.childNodes) {
      let paragraph;
      let textRuns = [];

      switch (node.tagName) {
        case "P":
          textRuns = handleChildNodes(node);
          paragraph = createParagraph(textRuns);
          break;
        case "BR":
          paragraph = createParagraph([new TextRun("")]);
          break;
        case "H1":
          paragraph = createParagraph([new TextRun({ text: node.textContent || "", bold: true, size: 32 })]);
          break;
        case "H2":
          paragraph = createParagraph([new TextRun({ text: node.textContent || "", bold: true, size: 28 })]);
          break;
        case "UL":
          node.querySelectorAll("li").forEach((li) => {
            const listItemContent = li.textContent || "";
            paragraphs.push(createParagraph([new TextRun({ text: listItemContent })], { bullet: { level: 0 } }));
          });
          break;
        case "OL":
          node.querySelectorAll("li").forEach((li) => {
            const listItemContent = li.textContent || "";
            paragraphs.push(createParagraph([new TextRun({ text: listItemContent })], { numbering: { reference: "numbered-list", level: 0 } }));
          });
          break;
        case "IMG":
          // Use the pre-fetched image buffers and insert them into paragraphs
          if (node.src && node.src.startsWith("http")) {
              const imageBuffer = imageBuffers[imageIndex++]; // Get the next image buffer
      
              if (imageBuffer) {
                  // Create an Image object to get the dimensions
                  const img = new Image();
                  img.src = node.src; // Set the image source to the image URL
      
                  let newWidth = 0;
                  let newHeight = 0;
      
                  // Wrap image loading in a function that returns a promise
                  const imageLoaded = new Promise((resolve, reject) => {
                      img.onload = () => {
                          newWidth = img.width;
                          newHeight = img.height;
      
                          // Check if the image width exceeds the max width
                          const maxWidth = 500; // Set your max width here
                          if (newWidth > maxWidth) {
                              newHeight = (newHeight * maxWidth) / newWidth; // Maintain aspect ratio
                              newWidth = maxWidth;
                          }
                          resolve({ newWidth, newHeight }); // Resolve with new dimensions
                      };
      
                      img.onerror = (error) => {
                          reject(error); // Reject the promise if there's an error loading the image
                      };
                  });
      
                  // Now, ensure that we wait for the image to load
                  try {
                      const { newWidth, newHeight } = await imageLoaded; // Wait for the promise to resolve
                      // Create ImageRun with the final dimensions
                      const image = new ImageRun({
                          data: imageBuffer,
                          transformation: { width: newWidth, height: newHeight }, // Apply the calculated size
                      });
      
                      // Create paragraph for this image
                      paragraph = createParagraph([image]);
                  } catch (error) {
                      console.warn("Image loading failed:", error);
                  }
              } else {
                  console.warn("Image Buffer is empty or undefined.");
              }
          }
          break;

        default:
          textRuns = handleChildNodes(node);
          paragraph = createParagraph(textRuns);
          console.warn(`Unrecognized tag: <${node.tagName}>`);
      }

      if (paragraph) {
        paragraphs.push(paragraph);
      }
    }

    return paragraphs;
  };

  // Function to sort and filter submissions
  const sortAndFilterData = async (report) => {
    try {
      const { submissions } = splitItems({
        includeSubmissions: true,
      });

      // Check if filtering is required
      const filterFlag = report.filter && Array.isArray(report.filter);
      const filteredData = filterFlag
        ? processFilter(submissions, report.filter)
        : [...submissions];
  
      // Check if sorting is defined N.B. ToDo perhaps use the built in sorting function
      if (!report.sort || !report.sort.column) {
        // If no sorting is defined, return filtered data as-is
        return filteredData;
      }
  
      // Extract sorting details
      const { column, direction = "asc", number = false } = report.sort;
  
      // Sort the submissions
      const sortedSubmissions = filteredData.sort((a, b) => {
        let valueA = getNestedPropertyValue(a, column);
        let valueB = getNestedPropertyValue(b, column);
  
        // Handle number sorting
        if (number) {
          valueA = valueA ? parseInt(valueA, 10) : Infinity;
          valueB = valueB ? parseInt(valueB, 10) : Infinity;
        } else {
          // Handle alphabetical sorting
          valueA = valueA ? String(valueA).toLowerCase() : '';
          valueB = valueB ? String(valueB).toLowerCase() : '';
  
          if (valueA === '' && valueB !== '') return 1;
          if (valueB === '' && valueA !== '') return -1;
        }
  
        // Sort based on direction
        if (number) {
          return direction === "asc" ? valueA - valueB : valueB - valueA;
        } else {
          return direction === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
        }
      });
  
      return sortedSubmissions;
  
    } catch (error) {
      console.error("Error processing report data:", error);
      return [];
    }
  };
  
  // Generic function to process report data
  const processReportData = async (report, testMode = false, limit = 15) => {
    try {
      // Step 1: Filter and sort the submissions
      const filteredData = await sortAndFilterData(report);

      console.log("filteredData", filteredData);

      // Step 2: Limit the number of submissions to process if testMode is enabled
      const submissionsToProcess = testMode ? filteredData.slice(0, limit) : filteredData;

      // Step 3: Generate HTML content for each submission
      const processedData = await Promise.all(
        submissionsToProcess.map(async (submission, index) => {
          const htmlContent = await generateHTMLFromTemplate(report, submission);
          return {
            content: htmlContent, // HTML string for this submission
            index: index, // Index for tracking
          };
        })
      );

      return processedData;
    } catch (error) {
      console.error("Error in processReportData:", error);
      return [];
    }
  };

  // New helper in DownloadOptions.jsx
  const resolveDisplayValue = (viewfieldsInstance, field, rawValue) => {
    // 1) Nullish → ""
    if (rawValue === undefined || rawValue === null || rawValue === "") return "";

    // 2) Ask ViewFields for authoritative options
    const { flatOptions, treeOptions } = viewfieldsInstance.resolveOptionsForField(field);

    // --- helpers ---
    const toKey = (v) => String(v).toLowerCase();

    // Build a fast map when flat options exist
    let flatMap = null;
    if (Array.isArray(flatOptions)) {
      flatMap = new Map();
      for (const o of flatOptions) {
        flatMap.set(toKey(o.value), o.name);
      }
    }

    // For tree, flatten to a map once
    let treeMap = null;
    if (!flatMap && Array.isArray(treeOptions)) {
      treeMap = new Map();
      const stack = [...treeOptions];
      while (stack.length) {
        const n = stack.pop();
        treeMap.set(toKey(n.value), n.name);
        if (Array.isArray(n.children)) stack.push(...n.children);
      }
    }

    // 3) Resolve a single scalar
    const resolveOne = (v) => {
      const k = toKey(v);
      if (flatMap && flatMap.has(k)) return flatMap.get(k);
      if (treeMap && treeMap.has(k)) return treeMap.get(k);
      return v; // fallback to raw
    };

    // 4) Handle multi-select values (arrays)
    if (Array.isArray(rawValue)) {
      // If your data stores array of primitives, this just works.
      // If it stores objects (e.g. { value, label }), map to value first.
      return rawValue.map(resolveOne).join(", ");
    }

    // 5) Scalar
    return resolveOne(rawValue);
  };


  // Render HTML using Handlebars for each submission
  const generateHTMLFromTemplate = async (report, submission) => {
    // Create a deep copy of the submission to avoid modifying read-only properties
    const submissionCopy = deepCopy(submission);

    // Parse meta.owner if it's a string in the deep copy
    if (typeof submissionCopy.meta?.owner === "string") {
      try {
        submissionCopy.meta.owner = JSON.parse(submissionCopy.meta.owner);
      } catch (error) {
        console.error("Error parsing meta.owner:", error);
      }
    }

    // Check if the files array exists and has any files in it
    if (submission.files && submission.files.length > 0) {
      // Fetch image data (base64 or signed URLs) for images, but only if they exist
      const images = await generateImagesForTemplate(submission);

      // Only add images to the submission copy if they exist
      Object.assign(submissionCopy, images);
    }

    // Step 1: Check if review data is required
    if (report.attributes?.includeReviewData === true && typeof report.attributes.numReviews === "number") {
      // Step 2: Find reviews associated with this submission
      let associatedReviews = allSubmissions.filter(
        (review) => (review.submission?.id || review.data?.submission?.id) === submission.id
      );

      // Step 3: Ensure exactly numReviews reviews are included
      const numReviews = report.attributes.numReviews;
      while (associatedReviews.length < numReviews) {
        associatedReviews.push({ data: {} }); // Add blank review placeholders
      }
      associatedReviews = associatedReviews.slice(0, numReviews); // Trim to numReviews

      // Step 4: Push the reviews into the submission
      submissionCopy.reviews = associatedReviews;
    }

    // Compile the template using Handlebars
    const compiledTemplate = Handlebars.compile(report.template);

    // Generate the final HTML by invoking the compiled template with the data
    const finalHTML = compiledTemplate(submissionCopy);

    return finalHTML;
  };

  
  // Function to generate images for the template (fetching from S3 or other sources)
  const generateImagesForTemplate = async (submission) => {
    const images = {};

    try {
      // Image keys are the values of dataKey (e.g., "image_1", "image_2", etc.)
      const imageKeys = ['image_1', 'image_2', 'image_3', 'image_4', 'image_5'];

      for (let key of imageKeys) {
        const file = submission.files.find(file => file.dataKey === key);  // Find file with matching dataKey

        if (file) {
          const fileKey = file.fileKey;

          // Use the Handlebars helper for generating the signed URL
          const signedUrl = await getUrl({ key: fileKey });

          // Add the signed URL to the images object
          images[`${key}_url`] = signedUrl.url.href;
        } else {
          //console.log(`No file found for ${key}`);
        }
      }
    } catch (error) {
      console.error("Error generating images for template:", error);
    }

    return images;
  };

  // Helper function to add paragraphProperties with spacing after
  const createParagraph = (children, options = {}) => {
    return new Paragraph({
      spacing: {
        before: 240,  // Define space before (example: 5000 TWIPs)
        after: 240,   // Define space after (example: 5000 TWIPs)
      },
      children,
      ...options,  // Additional paragraph options (bold, bullet, etc.)
    });
  };

  // Utility function to create a deep copy of an object
  const deepCopy = (obj) => {
    return JSON.parse(JSON.stringify(obj));
  };
    
  const generateDocxBook = async (processedData) => {
    const docSections = [];

    // Iterate through processedData and await parsing of HTML content
    for (const [index, submission] of processedData.entries()) {
      const docxParagraphs = await parseHtmlBodyToDocx(submission.content); // Ensure awaiting here to resolve image promises

      // Ensure valid paragraphs are added to docSections
      if (docxParagraphs && Array.isArray(docxParagraphs) && docxParagraphs.length > 0) {

        // Add a page break between submissions
        if (index > 0) {
          const pageBreakParagraph = getBlankParagraph();
          docSections.push(pageBreakParagraph);
        }

        // Add the parsed paragraphs to the document sections
        docSections.push(...docxParagraphs);
      } else {
        console.warn("No valid paragraphs for submission at index:", index);
      }
    }

    // Ensure docSections is an array before passing to the Document constructor
    if (docSections.length === 0) {
      throw new Error("docSections is empty or invalid.");
    }

    // Return the docSections for DOCX creation
    return docSections;
  };

  const generatePdfBook = (processedData) => {
    // Create a container element to store the generated HTML
    const htmlContainer = document.createElement('div');

    processedData.forEach((submission) => {
      // Append submission content to the container
      htmlContainer.innerHTML += submission.content + '<div style="page-break-before: always;"></div>';
    });

    // Return the container element
    return htmlContainer;
  };

  function generateHTMLDoc(processedData) {
    if (!Array.isArray(processedData)) {
      throw new Error("Input must be an array of objects containing HTML strings.");
    }
  
    // Start the HTML document with required opening tags
    let htmlDoc = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Generated HTML Document</title>
        <style>
          table, th, td { border: 1px solid black; border-collapse: collapse; padding: 5px; }
          h1 { font-size: 24px; }
          p { margin: 10px 0; }
        </style>
      </head>
      <body>
      `;
      
        // Add each HTML string from the processedData array into the body
        processedData.forEach((item, index) => {
          if (typeof item?.content !== "string") {
            throw new Error(`Item at index ${index} does not contain valid HTML in its 'content' property.`);
          }
          htmlDoc += item.content; // Append the HTML string from the 'content' property to the document body
        });
        // Close the HTML document
        htmlDoc += `
      </body>
      </html>
      `;

    // Return the final HTML document as a string
    return htmlDoc;
  }

  // Generic function to process a single submission
  const processSubmissionData = async (template, submission, fileName) => {
    try {
      let applicantSurname = "";

      // Build HTML from a **raw template string** + single submission
      const submissionCopy = deepCopy(submission);
      if (typeof submissionCopy.meta?.owner === "string") {
        try { submissionCopy.meta.owner = JSON.parse(submissionCopy.meta.owner); } catch {}
      }
      if (submissionCopy.files?.length) {
        const images = await generateImagesForTemplate(submissionCopy);
        Object.assign(submissionCopy, images);
      }

      // Compile raw template string
      const compiled = Handlebars.compile(template);
      const htmlContent = compiled(submissionCopy);

      const htmlContainer = document.createElement('div');
      htmlContainer.innerHTML = htmlContent;

      // Convert `.file-link` placeholders to signed URLs
      const fileLinks = htmlContainer.querySelectorAll('.file-link');
      for (const link of fileLinks) {
        const fileKey = link.getAttribute('file-key');
        const dataKey = link.getAttribute('data-key');
        const fileExtension = fileKey.split('.').pop();
        const applicationNumber = submission.meta?.trackingNumber;

        switch (viewType) {
          case ViewType.SUBMISSIONS:
          case ViewType.SUBMISSIONS_UNDER_REVIEW:
            applicantSurname = submission.meta?.owner?.familyName || 'Unknown';
            break;
          case ViewType.REVIEWS:
            applicantSurname = submission.data?.submission?.meta?.owner?.familyName || 'Unknown';
            break;
          default:
            throw new Error(`Unsupported view type: ${viewType}`);
        }

        const formattedFileName = `Application_${applicationNumber}_${applicantSurname}_${dataKey}.${fileExtension}`;
        try {
          const signedUrlObj = await getUrl({ key: fileKey });
          const signedUrl = signedUrlObj.url?.href || signedUrlObj.href;
          link.innerHTML = `<a href="${signedUrl}" target="_blank" download="${formattedFileName}">Click to download</a>`;
        } catch (err) {
          console.error(`Error generating signed URL for ${fileKey}:`, err);
          link.innerHTML = 'Error generating link';
        }
      }

      const base = fileName || "submission";
      const pdfName = `${base}_${(submission.meta?.trackingNumber || submission.id)}_${applicantSurname}`;
      const options = {
        margin: [20, 30, 10, 30],
        filename: `${pdfName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().from(htmlContainer).set(options).save();
    } catch (error) {
      console.error("Error processing submission data:", error);
    }
  };


  // Helper function to construct file names
  const constructFileName = (row, fileData, index) => {
    let fileName = "";

    // Add tracking number if it exists
    if (row.meta.trackingNumber) {
      fileName += `${String(row.meta.trackingNumber).padStart(4, '0')}_`;
    }

    // Handle row.meta.owner
    let ownerData = row.meta.owner;
    if (typeof ownerData === 'string') {
      try {
        ownerData = JSON.parse(ownerData);
      } catch (error) {
        console.error("Error parsing owner data:", error);
        ownerData = null;  // Ensure that ownerData is null in case of a parse failure
      }
    }

    // Add the owner's name information to the file name
    if (ownerData) {
      if (ownerData.name) {
        fileName += `${ownerData.name}_`; // Use name directly if it exists
      } else {
        const familyName = ownerData.familyName || "";
        const givenName = ownerData.givenName || "";
        if (familyName) fileName += `${familyName}`; // Add family name if it exists
        if (givenName) fileName += `_${givenName}`; // Add given name only if it exists and there's no "name"
      }
    }

    // Append column header name
    fileName += `_Image ${index}`;

    // Get the file extension from fileKey (e.g., ".pdf", ".doc", etc.)
    const fileExtension = fileData.fileKey.slice(fileData.fileKey.lastIndexOf('.'));

    // Add file extension
    fileName += `${fileExtension}`;

    return fileName;
  };


  const downloadExcel = async (data, label) => {
    // Build sheet from plain values first
    const ws = XLSX.utils.json_to_sheet(
      data.map(row => {
        const out = {};
        for (const [k, v] of Object.entries(row)) {
          // Convert hyperlink objects back to a plain visible label; we’ll re-inject hyperlinks later
          if (v && typeof v === "object" && ("f" in v || "l" in v)) {
            out[k] = "Download File";
          } else {
            out[k] = v;
          }
        }
        return out;
      })
    );

    // Re-inject hyperlinks into the worksheet (SheetJS link format)
    // Expect earlier pipeline to mark file cells with a known key pattern, or embed `{ l: { Target, Tooltip } }`
    data.forEach((row, rIdx) => {
      Object.entries(row).forEach(([k, v]) => {
        if (v && typeof v === "object" && v.f) {
          const cellAddr = XLSX.utils.encode_cell({ r: rIdx + 1, c: Object.keys(row).indexOf(k) });
          if (!ws[cellAddr]) ws[cellAddr] = { t: "s", v: "Download File" };
          // Parse =HYPERLINK("url","label")
          const m = /^=HYPERLINK\("([^"]+)"\s*,\s*"([^"]+)"\)$/.exec(v.f);
          if (m) {
            ws[cellAddr].l = { Target: m[1], Tooltip: m[2] };
            ws[cellAddr].t = "s";
            ws[cellAddr].v = m[2];
          }
        }
      });
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const filename = `${projectCode}_${label}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  /*
  // Function to download Excel file
  const downloadExcel = async (data, label ) => {
    console.log("Data Source: ", data);

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    // Define the filename using projectCode and viewName
    const filename = `${projectCode}_${label}.xlsx`;

    XLSX.writeFile(wb, filename);
  }
*/
  // Placeholder for downloading data as CSV
  const downloadCSV = (dataSource) => {
    console.log("Downloading as CSV...");
    console.log("Data Source: ", dataSource);
    // Implement CSV download logic here
    return;
  };

  // Placeholder for downloading data as PDF
  const downloadPDF = (dataSource, fileName) => {
    // Define the PDF options
    const options = {
      margin: 1,
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    // Generate the PDF from the container's HTML
    html2pdf().from(dataSource).set(options).save();
  };

  // Generate DOCX from parsed content
  const downloadDOCX2 = (contentArray, fileName) => {
    try {
      console.log("Downloading as DOCX...");
  
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: contentArray,
          },
        ],
      });
  
      // Generate the .docx file and save it
      Packer.toBlob(doc)
        .then((blob) => {
          saveAs(blob, fileName);
        })
        .catch((err) => {
          console.error("Error generating Word document", err);
        });
    } catch (error) {
      console.error("Error processing report:", error);
    }
  };

  // Placeholder for downloading data as DOCX
  const downloadDOCX = (htmlContentArray) => {
    try {
      console.log("Downloading as DOCX...");
  
      // Cleanly create a new Document object
      const doc = new Document({
        sections: [
          {
            properties: {},  // Ensure no undefined properties are set
            children: htmlContentArray.map(htmlContent => {
              if (htmlContent.includes("<h1>")) {
                const text = htmlContent.replace(/<\/?h1>/g, "");  // Remove <h1> tags
                return new Paragraph({
                  children: [new TextRun({ text, bold: true, size: 32 })],  // Add Heading 1 style
                });
              } else if (htmlContent.includes("<p>")) {
                const text = htmlContent.replace(/<\/?p>/g, "");  // Remove <p> tags
                return new Paragraph({
                  children: [new TextRun({ text })],  // Add paragraph text
                });
              }
              console.log(doc);  // Check the doc object to see if it has any undefined fields

              return new Paragraph();
            }),
          },
        ],
      });
      console.log("Final doc", doc);  // Check the doc object to see if it has any undefined fields

      // Generate the .docx file and save it
      Packer.toBlob(doc)
        .then((blob) => {
          saveAs(blob, 'generated_report.docx');
        })
        .catch((err) => {
          console.error("Error generating Word document", err);
        });
    } catch (error) {
      console.error("Error processing report:", error);
    }
  };   

  // Placeholder for downloading data as DOC
  const downloadDOC = (dataSource) => {
    console.log("Downloading as DOC...");
    console.log("Data Source: ", dataSource);
  
    // Ensure that dataSource is an array and has content
    if (!Array.isArray(dataSource) || dataSource.length === 0) {
      throw new Error("No content available for DOC download.");
      return;
    }
  
    // Create a new document with an initial empty section
    const doc = new Document({
      sections: [{
        children: dataSource.map(content => 
          new Paragraph({
            children: [
              new TextRun({
                text: content,  // Add content from the template output
                break: 1,  // This ensures new lines after each entry
              }),
            ],
          })
        ),
      }],
    });
  
    // Pack the document and trigger a download
    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, "report.docx");  // Save the file as "report.docx"
    }).catch((err) => {
      throw new Error("Error generating Word document", err);
    });
  };

  // Placeholder for downloading data as HTML
  const downloadHTML = (dataSource) => {
    console.log("Downloading as HTML...");
    console.log("Data Source: ", dataSource);
  
    // Create a blob from the HTML string
    const blob = new Blob([dataSource], { type: "text/html" });
  
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
  
    // Create a temporary anchor element
    const a = document.createElement("a");
    a.href = url;
    a.download = "document.html"; // Set the desired file name
  
    // Programmatically click the anchor to trigger the download
    a.click();
  
    // Clean up the URL object
    URL.revokeObjectURL(url);
  };

  const generatePdf = async (output) => {
    const doc = new jsPDF();

    // For basic text
    doc.text('Generated PDF', 10, 10);
    
    // If you want to add content dynamically
    doc.text(output, 10, 20);
  
    // Save the PDF
    doc.save('report.pdf');
  };

  const generateHtml = (output) => {
    console.log(output); // This will print the HTML to the console
    // Alternatively, you could write it to a file using Node.js's fs module
    // fs.writeFileSync('output.html', output);
  };

  const generateDoc = (output) => {
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [new TextRun(output)],
          }),
        ],
      }],
    });

    Packer.toBlob(doc).then(blob => {
      saveAs(blob, "report.docx");
    });
  };

  // Helper function to download a file from S3 (reverting to key-based approach)
  const fetchFile = async (fileKey, fileName) => {
    try {
      // Revert to using `key` to match the old working behavior
      const signedUrl = await getUrl({ key: fileKey });
      const response = await fetch(signedUrl.url.href);

      if (!response.ok) {
        throw new Error(`Failed to fetch file ${fileName}, response status: ${response.status}`);
      }

      const blob = await response.blob();
      return { blob, fileName };
    } catch (err) {
      console.error(`Error downloading file ${fileName}:`, err);
      return null; // Return null if there's an error to continue processing
    }
  };

  // Function to build ZIP for all files in all rows
  const downloadZIP = async (fieldData, rowData) => {
    console.log("fieldData", fieldData);
    console.log("rowData", rowData);
    const zip = new JSZip();
    let totalFiles = 0;
    let missingFiles = []; // To track missing files

    for (const row of rowData) {
      if (!row.files || row.files.length === 0) continue; // Skip if no files exist in the row

      for (const field of fieldData) {
        if (field.fieldType !== 'file') continue; // Skip non-file columns

        // Find the file in row.files based on the column field
        const fileData = row.files.find(file => file.dataKey && file.dataKey.toLowerCase().trim() === field.field.toLowerCase().trim());

        if (!fileData || !fileData.fileKey) continue; // Skip if no matching file is found

        // Call the helper function to construct the file name
        const fileName = constructFileName(row, fileData, field.headerName);

        // Fetch the file and add it to the ZIP
        const fileResult = await fetchFile(fileData.fileKey, fileName);
        if (fileResult) {
          zip.file(fileResult.fileName, fileResult.blob); // Add file to ZIP
          totalFiles++;
        } else {
          // Track missing files
          missingFiles.push({ fileName, fileKey: fileData.fileKey });
        }
      }
    }

    // Finalize and download the ZIP file
    if (totalFiles > 0) {
      zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } })
        .then((content) => {
          saveAs(content, `${projectCode}_${viewLabel}_files.zip`);
        })
        .catch((err) => {
          console.error("Error generating ZIP file:", err);
        });
    }

    // Log missing files if any
    if (missingFiles.length > 0) {
      console.warn(`There were ${missingFiles.length} missing files:`, missingFiles);
    }
  };

  //Bespoke report handler
  const processBespokeReport = async (report) => {
    switch (report.name) {
      case "File List PhySoc":
        return processPhySocFileList(report);
      case "Author Report PhySoc":
        return processPhySocAuthorsReport(report);
      case "Submission Upload PhySoc":
        return processPhySocSubmissionUploadReport(report);
      default:
        throw new Error(`Unsupported bespoke report: ${report.name}`);
    } 
  }

  //Bespoke report of PhySoc - File list.
  const processPhySocFileList = async (report) => {    
    const { submissions } = splitItems({
      includeSubmissions: true,
    });
    const fieldsSource = fields.filter(field => field.fieldType === "file");
    const downloadFields = report.fields;
    const filterFlag = report.filter && Array.isArray(report.filter); // Check if any item has the "filter" property
    const filteredRows = filterFlag ? processFilter(submissions, report.filter) : [...submissions];
    
    // Custom logic for the file-based report
    const processedRows = filteredRows.flatMap((row) => {
      if (row.files && row.files.length > 0) {
        return row.files.map((file, index) => {
          let newRow = {};
  
          // Handle the first field: "ID"
          const firstField = downloadFields.find(field => field.headerName === "ID");
          if (firstField && firstField.fieldType === "fixed") {
            newRow[firstField.headerName] = firstField.value; // Fixed value
          }
  
          // Handle the second field: "ArticleID"
          const secondField = downloadFields.find(field => field.headerName === "ArticleID");
          if (secondField) {
            // Extract value using dataSource (row.data.code)
            const dataSourceValue = getNestedPropertyValue(row, secondField.dataSource);
            const prefix = secondField.prefix || "";
            newRow[secondField.headerName] = `${prefix}${dataSourceValue || ""}`;
          }
  
          // Handle the third field: File Index (ImageNo)
          const thirdField = downloadFields.find(field => field.headerName === "ImageNo");
          if (thirdField && thirdField.fieldType === "index") {
            newRow[thirdField.headerName] = thirdField.zeroBased ? index : index + 1;
          }
  
          // Handle the fourth field: File Name
          const fourthField = downloadFields.find(field => field.headerName === "FileName");
          if (fourthField && fourthField.fieldType === "File.Name") {
            // Call the helper function to construct the file name
            newRow[fourthField.headerName] = constructFileName(row, file, index + 1);
          }
  
          return newRow;
        });
      } else {
        return []; // If no files, skip this row
      }
    });
      
    // Apply sorting after processing, dynamically based on the sort.field value
    if (report.sort && report.sort.field) {
      const sortField = report.sort.field;  // Dynamic field name (e.g., 'AbsID', 'data.code', etc.)
      const sortDirection = report.sort.direction || 'asc';
  
      // Perform sorting after processing the rows
      processedRows.sort((a, b) => {
        const valueA = getNestedPropertyValue(a, sortField);
        const valueB = getNestedPropertyValue(b, sortField);
  
        // Handle number vs string sorting
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sortDirection === 'asc' 
            ? valueA - valueB
            : valueB - valueA;
        } else {
          // If it's a string, use localeCompare
          const valA = String(valueA || '').toLowerCase();
          const valB = String(valueB || '').toLowerCase();
          return sortDirection === 'asc' 
            ? valA.localeCompare(valB) 
            : valB.localeCompare(valA);
        }
      });
    }
  
    return processedRows;
  };
  
  //Bespoke report of PhySoc - Author's report.
  const processPhySocAuthorsReport = async (report) => {
    const { submissions } = splitItems({
      includeSubmissions: true,
    });
    const downloadFields = report.fields;
  
    let filteredRows = [];
    const filterFlag = report.filter && Array.isArray(report.filter);
    filteredRows = filterFlag ? processFilter(submissions, report.filter) : [...submissions];
  
    console.log("filteredRows", filteredRows);
  
    // Custom logic for the authors-based report (process after sorting)
    const processedRows = filteredRows.flatMap((row) => {
      if (row.data.authors && row.data.authors.length > 0) {
        return row.data.authors.map((author, index) => {
          let newRow = {};
  
          // Handle the first field: "ID"
          const firstField = downloadFields.find(field => field.headerName === "ID");
          if (firstField && firstField.fieldType === "fixed") {
            newRow[firstField.headerName] = firstField.value; // Fixed value
          }
  
          // Handle the second field: "AbsID" (or dynamic field)
          const secondField = downloadFields.find(field => field.headerName === "AbsID");
          if (secondField) {
            // Extract value using dataSource (row.data.code) and apply prefix
            const dataSourceValue = getNestedPropertyValue(row, secondField.dataSource);
            const prefix = secondField.prefix || "";
            newRow[secondField.headerName] = `${prefix}${dataSourceValue || ""}`;
          }
  
          // Handle the third field: Author's givenName initials (capitalized)
          const thirdField = downloadFields.find(field => field.headerName === "Initials");
          if (thirdField && author.givenName) {
            const initials = author.givenName
              .split(" ")
              .map(word => word.charAt(0).toUpperCase())
              .join("");
            newRow[thirdField.headerName] = initials;
          }
  
          // Handle the fourth field: Author's familyName
          const fourthField = downloadFields.find(field => field.headerName === "Surname");
          if (fourthField && author.familyName) {
            newRow[fourthField.headerName] = author.familyName;
          }
  
          return newRow;
        });
      } else {
        return [];
      }
    });
  
    // Apply sorting after processing, dynamically based on the sort.column value
    if (report.sort && report.sort.field) {
      const sortField = report.sort.field;  // Dynamic field name (e.g., 'AbsID', 'data.code', etc.)
      const sortDirection = report.sort.direction || 'asc';
  
      // Perform sorting after processing the rows
      processedRows.sort((a, b) => {
        const valueA = getNestedPropertyValue(a, sortField);
        const valueB = getNestedPropertyValue(b, sortField);
  
        // Handle number vs string sorting
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sortDirection === 'asc' 
            ? valueA - valueB
            : valueB - valueA;
        } else {
          // If it's a string, use localeCompare
          const valA = String(valueA || '').toLowerCase();
          const valB = String(valueB || '').toLowerCase();
          return sortDirection === 'asc' 
            ? valA.localeCompare(valB) 
            : valB.localeCompare(valA);
        }
      });
    }
    return processedRows;
  };   
   
  //Bespoke report of PhySoc - Submission upload report.
  const processPhySocSubmissionUploadReport = async (report) => {
    const { submissions } = splitItems({
      includeSubmissions: true,
    });
    const downloadFields = report.fields;
  
    let filteredRows = [];
    const filterFlag = report.filter && Array.isArray(report.filter);
    filteredRows = filterFlag ? processFilter(submissions, report.filter) : [...submissions];
  
    console.log("filteredRows", filteredRows);
  
    // Custom logic for the authors-based report (process after sorting)
    const processedRows = filteredRows.flatMap((row) => {
        return row.data.authors.map((author, index) => {
          let newRow = {};
  
          // Handle the first field: "ID"
          const firstField = downloadFields.find(field => field.headerName === "ID");
          if (firstField && firstField.fieldType === "fixed") {
            newRow[firstField.headerName] = firstField.value; // Fixed value
          }
  
          // Handle the second field: "AbsID" (or dynamic field)
          const secondField = downloadFields.find(field => field.headerName === "AbsID");
          if (secondField) {
            // Extract value using dataSource (row.data.code) and apply prefix
            const dataSourceValue = getNestedPropertyValue(row, secondField.dataSource);
            const prefix = secondField.prefix || "";
            newRow[secondField.headerName] = `${prefix}${dataSourceValue || ""}`;
          }
  
          // Handle the third field: Author's givenName initials (capitalized)
          const thirdField = downloadFields.find(field => field.headerName === "Initials");
          if (thirdField && author.givenName) {
            const initials = author.givenName
              .split(" ")
              .map(word => word.charAt(0).toUpperCase())
              .join("");
            newRow[thirdField.headerName] = initials;
          }
  
          // Handle the fourth column: Author's familyName
          const fourthField = downloadFields.find(field => field.headerName === "Surname");
          if (fourthField && author.familyName) {
            newRow[fourthField.headerName] = author.familyName;
          }
  
          return newRow;
        });      
    });
  
    // Apply sorting after processing, dynamically based on the sort.field value
    if (report.sort && report.sort.field) {
      const sortField = report.sort.field;  // Dynamic field name (e.g., 'AbsID', 'data.code', etc.)
      const sortDirection = report.sort.direction || 'asc';
  
      // Perform sorting after processing the rows
      processedRows.sort((a, b) => {
        const valueA = getNestedPropertyValue(a, sortField);
        const valueB = getNestedPropertyValue(b, sortField);
  
        // Handle number vs string sorting
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sortDirection === 'asc' 
            ? valueA - valueB
            : valueB - valueA;
        } else {
          // If it's a string, use localeCompare
          const valA = String(valueA || '').toLowerCase();
          const valB = String(valueB || '').toLowerCase();
          return sortDirection === 'asc' 
            ? valA.localeCompare(valB) 
            : valB.localeCompare(valA);
        }
      });
    }
    return processedRows;
  };

  //Helper function to get the value from a submission depending on its data source
  const getValueFromObject = (obj, field) => {
    try {
      // direct (root key)
      if (field.dataSource && !field.dataSource.includes(".")) {
        const v = obj[field.dataSource];
        return v !== undefined ? v : "";
      }

      // meta.owner
      if (field.dataSource && field.dataSource.startsWith("meta.owner")) {
        let owner = obj.meta?.owner;
        if (typeof owner === "string") {
          try { owner = JSON.parse(owner); } catch { owner = null; }
        }
        if (owner && typeof owner === "object") {
          let f = field.field;
          if (f === "first_name") f = "givenName";
          if (f === "last_name")  f = "familyName";
          return owner[f] || "";
        }
        return "";
      }

      // submission.meta.owner
      if (
        field.dataSource &&
        (field.dataSource.startsWith("submission.meta.owner") ||
          // TODO(dan): data.submission.meta.owner relies on the review-side
          // data.submission write-back, which has been removed (reviews are now
          // stored as JSON in S3, not embedded in Submissions rows). This will read
          // stale/missing owner data going forward — needs to source owner data from
          // the review's S3 asset (or its linkedData) instead.
          field.dataSource.startsWith("data.submission.meta.owner"))
      ) {
        const owner = obj.submission?.meta?.owner || obj.data?.submission?.meta?.owner;
        if (owner && typeof owner === "object") {
          let f = field.field;
          if (f === "app_first_name") f = "givenName";
          if (f === "app_last_name")  f = "familyName";
          if (f === "app_email")      f = "email";
          return owner[f] || "";
        }
        return "";
      }

      // fixed / index (report schema uses fieldType)
      if (field.fieldType === "fixed" || field.fieldType === "index") {
        return field.value;
      }

      // generic nested get
      const raw = getNestedPropertyValue(obj, field.dataSource);

      // Option-display mapping via ViewFields/FieldOptions (authoritative)
      if (
        Array.isArray(field?.options) ||
        typeof field?.optionsSource === "string" ||
        typeof field?.optionsAssetKey === "string"
      ) {
        return resolveDisplayValue(viewfieldsInstance, field, raw);
      }

      return raw !== undefined ? raw : "";
    } catch (error) {
      const errMsg = `Error in getValueFromObject: ${error.message} <p>Column: ${JSON.stringify(field, null, 2)}</p><p>obj: ${JSON.stringify(obj, null, 2)}</p>`;
      console.error(errMsg);
      handleError(errMsg, appUser);
      return "";
    }
  };



  return (
      <div className={`report-island ${expanded ? 'show-list' : ''}`}>
        {Array.isArray(availableReports) && availableReports.length > 0 ? (
          availableReports.map((report) => (
            <button key={report.id} className="report-list-item" onClick={() => downloadReport(report)}>
              {report.name} - ({report.format})
            </button>
          ))
        ) : (
          <p>No bespoke reports</p>
        )}
      </div>
  );
};

const getNestedPropertyValue = (object, path) => {
  if (!object || typeof object !== 'object' || !path || typeof path !== 'string') return undefined;
  return path.split('.').reduce((acc, key) => (acc && typeof acc === 'object') ? acc[key] : undefined, object);
};

  // Helper to get and log title
  const getTitle = (row) => {
    const title = row.data?.title || "No title";
    
    return title ? new Paragraph({
      children: [new TextRun({ text: title, bold: true, size: 24 })],
    }) : new Paragraph({});
  };

  // Handle author extraction and affiliations
  const getAuthorsAndAffiliations = (row) => {
    const paragraphs = [];
    const authors = Array.isArray(row.data?.authors) ? row.data.authors : [];
    const affiliations = Array.isArray(row.data?.affiliations) ? row.data.affiliations : [];

    console.log("Extracted Authors:", authors);
    console.log("Extracted Affiliations:", affiliations);

    // Handle authors
    if (authors.length === 0) {
      paragraphs.push(new Paragraph({ children: [new TextRun({ text: "No authors available" })] }));
    } else {
      const authorRuns = [];
      authors.forEach((author, i) => {
        // Construct the author name from givenName and familyName
        const authorName = `${author.givenName || "Unknown"} ${author.familyName || "Author"}`;

        // Add the author's name to the run, underline if presenting
        authorRuns.push(new TextRun({
          text: authorName,
          underline: author.presenting || false,
        }));

        // Add superscripts for the author's affiliations
        if (Array.isArray(author.affiliations)) {
          author.affiliations.forEach((affIndex) => {
            const affiliationIndex = affIndex + 1; // Correct zero-based to one-based
            authorRuns.push(new TextRun({
              text: String(affiliationIndex),
              superScript: true, // Superscript for affiliation index
            }));
          });
        } else {
          console.warn(`Invalid or missing affiliations for author at index ${i}:`, author);
        }

        if (i < authors.length - 1) {
          authorRuns.push(new TextRun({ text: ", " }));
        }
      });
      paragraphs.push(new Paragraph({ children: authorRuns }));
    }

    // Add an empty paragraph between authors and affiliations
    paragraphs.push(new Paragraph({
      text: "",  // Empty paragraph for spacing between authors and affiliations
    }));

    // Handle affiliations
    if (affiliations.length === 0) {
      paragraphs.push(new Paragraph({ children: [new TextRun({ text: "No affiliations available" })] }));
    } else {
      const affiliationRuns = [];
      affiliations.forEach((aff, i) => {
        const affiliationIndex = i + 1;

        // Add affiliation index and institution, country on the same line
        affiliationRuns.push(new TextRun({
          text: `${affiliationIndex}`, // Index with superscript
          superScript: true,
        }));
        affiliationRuns.push(new TextRun({
          text: ` ${aff.institution}, ${aff.country}`, // Institution and country
        }));

        if (i < affiliations.length - 1) {
          affiliationRuns.push(new TextRun({ text: "; " })); // Separate affiliations with a semicolon
        }
      });

      // Add all affiliation runs to a single paragraph to keep them on the same line
      paragraphs.push(new Paragraph({ children: affiliationRuns }));
    }

    return paragraphs;
  };

  const applyStylingToSheet = (sheet, updatedFields) => {
    // Apply styles to the header row (row 1)
    const headerRow = sheet.getRow(1);
    headerRow.border = {
      top: { style: "thick", color: { argb: "FFFF0000" } },
      left: { style: "thick", color: { argb: "000000FF" } },
      bottom: { style: "thick", color: { argb: "F08080" } },
      right: { style: "thick", color: { argb: "FF00FF00" } },
    };

    headerRow.fill = {
      type: "pattern",
      pattern: "darkVertical",
      fgColor: { argb: "FFFF00" },
    };

    headerRow.font = {
      name: "Comic Sans MS",
      family: 4,
      size: 16,
      bold: true,
    };

    // Iterate over report.columns to apply styles based on the type
    updatedFields.forEach((column, index) => {
      const columnIndex = index + 1; // ExcelJS columns are 1-based

      // Identify the review columns based on source or field information
      if (column.source === "Review") {
        const reviewCol = sheet.getColumn(columnIndex);

        // Apply specific styling to all cells in this column
        reviewCol.eachCell((cell) => {
          // Apply background color or other styles based on the review number (1, 2, or 3)
          let backgroundColor;
          if (cell.address.includes("Review 1")) {
            backgroundColor = { argb: "FF00FF00" }; // Light green for Review 1
          } else if (cell.address.includes("Review 2")) {
            backgroundColor = { argb: "FF0000FF" }; // Light blue for Review 2
          } else if (cell.address.includes("Review 3")) {
            backgroundColor = { argb: "FFFF0000" }; // Light red for Review 3
          }

          // Apply fill to the cell
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: backgroundColor,
          };
        });
      }
    });
  };
    
  // Helper to get and log abstract code
  const getAbstractCode = (row, addPageBreak = false) => {
    const abstractCode = row.data?.code || row.meta?.trackingNumber?.toString() || row.id || "No abstract code";
    
    return new Paragraph({
      children: [new TextRun({ text: abstractCode, bold: true, size: 32 })],
      pageBreakBefore: addPageBreak,  // Add page break if needed
    });
  };

export default DownloadOptions;
